import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, CreditCard, CheckCircle2, Lock, Upload, Package } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("auth_token")}` });
const eur = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;
const EMPTY = { name: "", description: "", price: "", image_url: "", active: true };

export const LockedCard = ({ text, onUpgrade, testId }) => (
  <div className="rounded-2xl border border-slate-100 bg-white p-8 text-center shadow-sm" data-testid={testId}>
    <Lock className="mx-auto mb-3 h-8 w-8 text-slate-400" />
    <p className="mb-4 text-slate-700">{text}</p>
    <Button onClick={onUpgrade} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid={`${testId}-upgrade`}>Paket upgraden</Button>
  </div>
);

const ProductDialog = ({ pageId, product, onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(product ? { ...EMPTY, ...product, price: product.price != null ? Number(product.price).toFixed(2).replace(".", ",") : "" } : EMPTY); }, [product]);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/pages/${pageId}/upload-image`, fd, { headers: authHeaders() });
      setF((x) => ({ ...x, image_url: data.url }));
    } catch (err) { toast.error(err.response?.data?.detail || "Bild-Upload fehlgeschlagen"); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, price: parseFloat(String(f.price).replace(",", ".")) };
      if (product?.id) await axios.put(`${API}/pages/${pageId}/products/${product.id}`, body, { headers: authHeaders() });
      else await axios.post(`${API}/pages/${pageId}/products`, body, { headers: authHeaders() });
      toast.success("Produkt gespeichert"); onSaved(); onClose();
    } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Bitte Name und gültigen Preis angeben"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="product-dialog">
        <DialogHeader><DialogTitle>{product?.id ? "Produkt bearbeiten" : "Neues Produkt"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="product-name-input" /></div>
          <div><Label>Beschreibung</Label><Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="product-description-input" /></div>
          <div><Label>Preis in € (inkl. MwSt.)</Label><Input inputMode="decimal" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} placeholder="19,90" data-testid="product-price-input" /></div>
          <div className="flex items-center gap-3">
            {f.image_url && <img src={f.image_url} alt="" className="h-14 w-14 rounded-lg object-cover" data-testid="product-image-preview" />}
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm hover:bg-slate-50">
              <Upload className="h-4 w-4" /> {f.image_url ? "Bild ändern" : "Bild hochladen"}
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} data-testid="product-image-input" />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="product-active-checkbox" /> Im Shop anzeigen</label>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="product-save-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Speichern</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PaymentsCard = ({ pageId, status, shipping, onReload }) => {
  const [busy, setBusy] = useState(false);
  const [fee, setFee] = useState(shipping);
  useEffect(() => setFee(Number(shipping || 0).toFixed(2).replace(".", ",")), [shipping]);
  const connect = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/pages/${pageId}/payments/connect`, {}, { headers: authHeaders() }); window.location.href = data.url; }
    catch (err) { toast.error(err.response?.data?.detail || "Stripe-Verbindung fehlgeschlagen"); setBusy(false); }
  };
  const saveFee = async () => {
    try { await axios.put(`${API}/pages/${pageId}/shop-settings`, { shipping_fee: parseFloat(String(fee).replace(",", ".")) || 0, enabled: true }, { headers: authHeaders() }); toast.success("Versandkosten gespeichert"); onReload(); }
    catch { toast.error("Ungültige Versandkosten"); }
  };
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm" data-testid="payments-card">
      <div className="mb-3 flex items-center gap-2 font-semibold text-slate-900"><CreditCard className="h-5 w-5 text-indigo-600" /> Zahlungen annehmen</div>
      {status?.charges_enabled ? (
        <p className="flex items-center gap-2 text-sm text-emerald-700" data-testid="payments-active"><CheckCircle2 className="h-4 w-4" /> Stripe ist verbunden – Kunden können auf deiner Seite per Karte, Apple Pay & Co. bezahlen. Das Geld geht direkt auf dein Konto.</p>
      ) : (
        <>
          <p className="mb-4 text-sm text-slate-600">Verbinde dein eigenes Stripe-Konto (kostenlos, ca. 5 Minuten). Danach können Kunden deine Produkte direkt auf der Webseite bezahlen – ohne Gebühren von uns.</p>
          <Button onClick={connect} disabled={busy} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="payments-connect-btn">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{status?.connected ? "Einrichtung bei Stripe fortsetzen" : "Mit Stripe verbinden"}
          </Button>
        </>
      )}
      <div className="mt-5 flex items-end gap-2 border-t border-slate-100 pt-4">
        <div className="flex-1"><Label>Versandkosten pro Bestellung (€, 0 = Abholung/kein Versand)</Label><Input inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value)} data-testid="shipping-fee-input" /></div>
        <Button variant="outline" onClick={saveFee} data-testid="shipping-fee-save">Speichern</Button>
      </div>
    </div>
  );
};

export function ShopSection({ pageId, onUpgrade }) {
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(false);
  const [status, setStatus] = useState(null);
  const [orders, setOrders] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/pages/${pageId}/products`, { headers: authHeaders() }).then((r) => setData(r.data)).catch((e) => e.response?.status === 402 ? setLocked(true) : toast.error("Shop konnte nicht geladen werden"));
    axios.get(`${API}/pages/${pageId}/payments/status`, { headers: authHeaders() }).then((r) => setStatus(r.data)).catch(() => {});
    axios.get(`${API}/pages/${pageId}/orders`, { headers: authHeaders() }).then((r) => setOrders(r.data.orders)).catch(() => {});
  }, [pageId]);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => {
    if (!window.confirm("Produkt wirklich löschen?")) return;
    await axios.delete(`${API}/pages/${pageId}/products/${id}`, { headers: authHeaders() }); toast.success("Produkt gelöscht"); load();
  };

  if (locked) return <LockedCard text="Online-Shop und Zahlungen sind im Pro- und Premium-Paket enthalten." onUpgrade={onUpgrade} testId="shop-locked" />;
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  return (
    <div className="space-y-6" data-testid="shop-dashboard">
      <PaymentsCard pageId={pageId} status={status} shipping={data.shop?.shipping_fee ?? 0} onReload={load} />
      <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-semibold text-slate-900"><Package className="h-5 w-5 text-indigo-600" /> Produkte ({data.products.length})</h3>
          <Button onClick={() => setEdit({})} size="sm" className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="product-add-btn"><Plus className="mr-1 h-4 w-4" /> Produkt</Button>
        </div>
        {data.products.length === 0 && <p className="text-sm text-slate-500" data-testid="products-empty">Noch keine Produkte. Füge dein erstes Produkt mit Bild, Beschreibung und Preis hinzu – es erscheint automatisch im Bereich „Unsere Produkte“ auf deiner Webseite.</p>}
        <ul className="divide-y divide-slate-100">
          {data.products.map((p) => (
            <li key={p.id} className="flex items-center gap-3 py-3" data-testid={`product-row-${p.id}`}>
              {p.image_url ? <img src={p.image_url} alt="" className="h-12 w-12 rounded-lg object-cover" /> : <div className="h-12 w-12 rounded-lg bg-slate-100" />}
              <div className="min-w-0 flex-1"><p className="truncate font-medium text-slate-900">{p.name}{!p.active && <span className="ml-2 text-xs text-slate-400">(ausgeblendet)</span>}</p><p className="text-sm text-slate-500">{eur(p.price)}</p></div>
              <Button variant="ghost" size="icon" onClick={() => setEdit(p)} data-testid={`product-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => remove(p.id)} className="text-red-600" data-testid={`product-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm" data-testid="orders-card">
        <h3 className="mb-3 font-semibold text-slate-900">Bestellungen ({orders.length})</h3>
        {orders.length === 0 ? <p className="text-sm text-slate-500">Noch keine bezahlten Bestellungen.</p> : (
          <ul className="divide-y divide-slate-100 text-sm">
            {orders.map((o) => (
              <li key={o.id} className="py-3" data-testid={`order-row-${o.id}`}>
                <div className="flex justify-between font-medium"><span>{o.customer?.name || o.customer?.email || "Kunde"}</span><span>{eur(o.total)}</span></div>
                <p className="text-slate-500">{o.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")} · {new Date(o.paid_at || o.created_at).toLocaleString("de-DE")}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
      <ProductDialog pageId={pageId} product={edit} onClose={() => setEdit(null)} onSaved={load} />
    </div>
  );
}
